import { graphql, useFragment } from "react-relay";
import type { portals_club$key } from "./__generated__/portals_club.graphql";
import type { portals_coursePortal$key } from "./__generated__/portals_coursePortal.graphql";
import type { portals_eventPortal$key } from "./__generated__/portals_eventPortal.graphql";
import type { portals_user$key } from "./__generated__/portals_user.graphql";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/icon";
import { Image } from "@/components/image";
import { cn } from "@/utils";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { isBefore, isAfter, format } from "date-fns";
import { ptBR } from "date-fns/locale/pt-BR";

export function EventPortal({ event }: { event: portals_eventPortal$key }) {
	const data = useFragment<portals_eventPortal$key>(
		graphql`
    fragment portals_eventPortal on Event {
      id

      title
      startDate
      endDate
      slug
    }
  `,
		event,
	);

	const isLive =
		isAfter(new Date(), new Date(data.startDate)) &&
		isBefore(new Date(), new Date(data.endDate));

	return (
		<Card
			className={cn(
				"w-full px-6 flex flex-row items-center justify-between h-[126px]",
				isLive && "border-red-600 border",
			)}
		>
			<div className="flex flex-row gap-3">
				<div className="flex flex-col items-center justify-center bg-accent aspect-square size-14 rounded">
					<span className="text-base/6 font-semibold text-accent-foreground">
						{format(new Date(data.startDate), "dd")}
					</span>
					<span className="text-xs uppercase font-medium text-accent-foreground/70">
						{format(new Date(data.startDate), "MMM", { locale: ptBR })}
					</span>
				</div>

				<div className="flex flex-col gap-1.5">
					{isLive ? (
						<span className="text-red-600 text-xs/5">Ao vivo agora</span>
					) : (
						<span className="text-muted-foreground text-xs/5">
							Evento em destaque
						</span>
					)}

					<span className="text-card-foreground text-base/6">{data.title}</span>

					<div className="flex items-center justify-start gap-1 text-muted-foreground">
						<Icon name="Clock9" className="size-3" />
						<span className="text-xs/5">
							{format(new Date(data.startDate), "HH:mm")}
							{" - "}
							{format(new Date(data.endDate), "HH:mm")}
						</span>
					</div>
				</div>
			</div>

			<div className="h-full flex items-end justify-end">
				<Link to="/eventos/$slug" params={{ slug: data.slug }}>
					<Button variant="outline">
						Visualizar
						<Icon name="ChevronRight" />
					</Button>
				</Link>
			</div>
		</Card>
	);
}

export function CoursePortal({ club }: { club: portals_coursePortal$key }) {
	const data = useFragment<portals_coursePortal$key>(
		graphql`
    fragment portals_coursePortal on CourseClass {
      id
      name
      active
      slug
      section {
        active
      }
      course {
        name
        slug
        active
        landscapeImage {
          signedUrl
        }
        portraitImage {
          signedUrl
        }
        category {
          orientation
          active
        }
      }
    }
  `,
		club,
	);

	const photoUrl =
		data.course.category.orientation === "LANDSCAPE"
			? data.course.landscapeImage?.signedUrl
			: data.course.portraitImage?.signedUrl;
	const aspect =
		data.course.category.orientation === "LANDSCAPE"
			? "aspect-video"
			: "aspect-7/10";

	if (
		!data.active ||
		!data.section.active ||
		!data.course.active ||
		!data.course.category.active
	) {
		return null;
	}

	return (
		<Card className="w-full py-0 overflow-hidden flex flex-row items-center justify-between h-[126px]">
			<div className="flex flex-row gap-3 h-full flex-1 items-center justify-start">
				{photoUrl ? (
					<Image src={photoUrl} className={cn(aspect, "h-[126px]")} />
				) : null}

				<div>
					<div className="flex flex-col gap-1.5 p-6">
						<span className="text-muted-foreground text-xs/5">
							Aula em destaque
						</span>

						<span className="text-card-foreground text-base/6">
							{data.name}
						</span>

						<span className="text-muted-foreground text-xs/5">
							{data.course.name}
						</span>
					</div>
				</div>
			</div>

			<div className="h-[126px] p-6 flex items-end justify-end">
				<Link
					to="/cursos/$course/$lesson"
					params={{ course: data.course.slug, lesson: data.slug }}
				>
					<Button variant="outline">
						Assistir Agora
						<Icon name="Play" />
					</Button>
				</Link>
			</div>
		</Card>
	);
}

export function Portals({
	club,
	user,
}: {
	club: portals_club$key;
	user?: portals_user$key | null;
}) {
	const match = useMatchRoute();
	const userData = useFragment<portals_user$key>(
		graphql`
    fragment portals_user on User
    @argumentDefinitions(club: { type: "String!" })
    {
      lastCompletedClass(club: $club) {
        id

        courseClass {
          ...on CourseContent {
            nextClass {
                ...portals_coursePortal
            }
          }
        }
      }
    }
  `,
		user,
	);
	const data = useFragment<portals_club$key>(
		graphql`
    fragment portals_club on Club {
      nextEvents: events(orderBy: CHRONOLOGICAL, filterBy: UPCOMING, first: 1) {
        edges {
          node {
            id
            ...portals_eventPortal
          }
        }
      }
    }
  `,
		club,
	);

	if (userData?.lastCompletedClass?.courseClass?.nextClass) {
		return (
			<CoursePortal club={userData.lastCompletedClass.courseClass.nextClass} />
		);
	}

	if (
		data.nextEvents.edges.filter((e) => Boolean(e.node)).length > 0 &&
		!match({ to: "/eventos" })
	) {
		return <EventPortal event={data.nextEvents.edges[0].node} />;
	}

	return null;
}
